'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { IconCalendar } from '@/components/flow/FlowIcons';

// ═══ Types ═══
interface UnifiedEvent {
    id: string;
    type: 'schedule' | 'flow_task' | 'flow_schedule';
    source: 'scheduler' | 'flow';
    title: string;
    date: string;
    status?: string;
    priority?: string;
    assignee?: string;
    projectName?: string;
    color: string;
    progress?: number;
}

interface CalendarData {
    year: number;
    month: number;
    totalCount: number;
    breakdown: {
        scheduler: number;
        flowTasks: number;
        flowSchedules: number;
    };
    events: Record<string, UnifiedEvent[]>;
}

type SourceFilter = 'all' | 'scheduler' | 'flow';

// ═══ Status badge helper ═══
function getStatusBadge(status?: string) {
    const map: Record<string, { label: string; bg: string; text: string }> = {
        todo: { label: '대기', bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' },
        'in-progress': { label: '진행', bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
        waiting: { label: '대기', bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' },
        in_progress: { label: '진행', bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
        feedback: { label: '피드백', bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
        done: { label: '완료', bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
        hold: { label: '보류', bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
        issue: { label: '이슈', bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
    };
    return map[status || ''] || null;
}

function getSourceIcon(source: 'scheduler' | 'flow') {
    return source === 'scheduler' ? '📋' : '🔄';
}

// ═══ Calendar Grid ═══
export default function UnifiedCalendarView() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState<CalendarData | null>(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<SourceFilter>('all');
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const sourceParam = filter === 'all' ? '' : `&source=${filter}`;
            const res = await fetch(`/api/calendar/unified?year=${year}&month=${month}${sourceParam}`);
            const json = await res.json();
            setData(json);
        } catch (e) {
            console.error('Failed to fetch unified calendar:', e);
        } finally {
            setLoading(false);
        }
    }, [year, month, filter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const goMonth = (delta: number) => {
        let newMonth = month + delta;
        let newYear = year;
        if (newMonth < 1) { newMonth = 12; newYear--; }
        if (newMonth > 12) { newMonth = 1; newYear++; }
        setYear(newYear);
        setMonth(newMonth);
        setSelectedDate(null);
    };

    const goToday = () => {
        const t = new Date();
        setYear(t.getFullYear());
        setMonth(t.getMonth() + 1);
        setSelectedDate(null);
    };

    // Build calendar grid
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const calendarDays: (number | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) calendarDays.push(null);
    for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);
    // Pad to full weeks
    while (calendarDays.length % 7 !== 0) calendarDays.push(null);

    const weeks: (number | null)[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
        weeks.push(calendarDays.slice(i, i + 7));
    }

    const getDateKey = (day: number) =>
        `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    const getEventsForDay = (day: number): UnifiedEvent[] => {
        if (!data?.events) return [];
        return data.events[getDateKey(day)] || [];
    };

    const selectedEvents = selectedDate && data?.events ? (data.events[selectedDate] || []) : [];
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

    return (
        <div className="flex-1 overflow-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <IconCalendar size={22} />
                    통합 캘린더
                </h1>
                <div className="flex items-center gap-2">
                    {/* Source filter */}
                    <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                        {(['all', 'scheduler', 'flow'] as SourceFilter[]).map(f => (
                            <button key={f} onClick={() => setFilter(f)}
                                className="px-3 py-1.5 text-xs font-medium transition-all"
                                style={{
                                    background: filter === f ? 'var(--accent-blue)' : 'transparent',
                                    color: filter === f ? '#fff' : 'var(--text-muted)',
                                }}>
                                {f === 'all' ? '전체' : f === 'scheduler' ? '📋 스케줄러' : '🔄 플로우'}
                            </button>
                        ))}
                    </div>
                    <Link href="/scheduler"
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                        style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
                        📅 스케줄러 열기
                    </Link>
                </div>
            </div>

            {/* Calendar Container */}
            <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                {/* Month Navigation */}
                <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <button onClick={() => goMonth(-1)} className="p-2 rounded-lg transition-all hover:opacity-80"
                        style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>
                        ◀
                    </button>
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                            {year}년 {monthNames[month - 1]}
                        </h2>
                        {(year !== now.getFullYear() || month !== now.getMonth() + 1) && (
                            <button onClick={goToday}
                                className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-white shadow-sm"
                                style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                                오늘
                            </button>
                        )}
                    </div>
                    <button onClick={() => goMonth(1)} className="p-2 rounded-lg transition-all hover:opacity-80"
                        style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>
                        ▶
                    </button>
                </div>

                {/* Stats bar */}
                {data && (
                    <div className="px-5 py-2 flex items-center gap-4 text-xs" style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-dim)' }}>
                        <span>📋 스케줄러: <strong style={{ color: 'var(--text-secondary)' }}>{data.breakdown.scheduler}</strong></span>
                        <span>🔄 플로우 업무: <strong style={{ color: 'var(--text-secondary)' }}>{data.breakdown.flowTasks}</strong></span>
                        <span>📅 플로우 일정: <strong style={{ color: 'var(--text-secondary)' }}>{data.breakdown.flowSchedules}</strong></span>
                        <span className="ml-auto font-bold" style={{ color: 'var(--text-muted)' }}>
                            총 {data.totalCount}건
                        </span>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                ) : (
                    <div className="flex flex-col lg:flex-row">
                        {/* Calendar Grid */}
                        <div className="flex-1">
                            {/* Day headers */}
                            <div className="grid grid-cols-7" style={{ borderBottom: '1px solid var(--border-color)' }}>
                                {dayNames.map((d, i) => (
                                    <div key={d} className="py-2 text-center text-xs font-bold"
                                        style={{
                                            color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : 'var(--text-muted)',
                                            borderRight: i < 6 ? '1px solid var(--border-color)' : 'none',
                                        }}>
                                        {d}
                                    </div>
                                ))}
                            </div>

                            {/* Weeks */}
                            {weeks.map((week, wIdx) => (
                                <div key={wIdx} className="grid grid-cols-7" style={{ borderBottom: wIdx < weeks.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                                    {week.map((day, dIdx) => {
                                        if (day === null) {
                                            return <div key={`empty-${dIdx}`} className="min-h-[100px] p-1"
                                                style={{ borderRight: dIdx < 6 ? '1px solid var(--border-color)' : 'none', background: 'var(--bg-input)', opacity: 0.3 }} />;
                                        }
                                        const dateKey = getDateKey(day);
                                        const dayEvents = getEventsForDay(day);
                                        const isToday = dateKey === todayStr;
                                        const isSelected = dateKey === selectedDate;

                                        return (
                                            <div key={dateKey}
                                                className="min-h-[100px] p-1.5 cursor-pointer transition-colors"
                                                style={{
                                                    borderRight: dIdx < 6 ? '1px solid var(--border-color)' : 'none',
                                                    background: isSelected ? 'rgba(99,102,241,0.08)' : isToday ? 'rgba(59,130,246,0.05)' : 'transparent',
                                                }}
                                                onClick={() => setSelectedDate(dateKey === selectedDate ? null : dateKey)}>
                                                {/* Day number */}
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-xs font-bold leading-none ${isToday ? 'w-6 h-6 rounded-full flex items-center justify-center text-white' : ''}`}
                                                        style={{
                                                            color: isToday ? '#fff' : dIdx === 0 ? '#ef4444' : dIdx === 6 ? '#3b82f6' : 'var(--text-secondary)',
                                                            background: isToday ? '#3b82f6' : 'transparent',
                                                        }}>
                                                        {day}
                                                    </span>
                                                    {dayEvents.length > 0 && (
                                                        <span className="text-[9px] px-1 py-0.5 rounded-full font-bold"
                                                            style={{ background: 'rgba(99,102,241,0.15)', color: '#6366f1' }}>
                                                            {dayEvents.length}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Event previews - max 3 */}
                                                <div className="space-y-0.5">
                                                    {dayEvents.slice(0, 3).map(evt => (
                                                        <div key={evt.id}
                                                            className="flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium truncate transition-all"
                                                            style={{
                                                                background: `${evt.color}15`,
                                                                color: 'var(--text-secondary)',
                                                                borderLeft: `2px solid ${evt.color}`,
                                                            }}>
                                                            <span className="opacity-60">{getSourceIcon(evt.source)}</span>
                                                            <span className="truncate">{evt.title.replace(/^[■*\s]+/, '')}</span>
                                                        </div>
                                                    ))}
                                                    {dayEvents.length > 3 && (
                                                        <div className="text-[9px] text-center font-medium" style={{ color: 'var(--text-dim)' }}>
                                                            +{dayEvents.length - 3}건 더
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>

                        {/* Detail Panel (selected date) */}
                        {selectedDate && (
                            <div className="w-full lg:w-80 flex-shrink-0" style={{ borderLeft: '1px solid var(--border-color)' }}>
                                <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <h3 className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                                        📅 {selectedDate.replace(/-/g, '.')}
                                    </h3>
                                    <button onClick={() => setSelectedDate(null)}
                                        className="text-xs p-1 rounded transition-all hover:opacity-70"
                                        style={{ color: 'var(--text-dim)' }}>
                                        ✕
                                    </button>
                                </div>
                                <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
                                    {selectedEvents.length === 0 ? (
                                        <div className="text-center py-10" style={{ color: 'var(--text-dim)' }}>
                                            <p className="text-3xl mb-2">📭</p>
                                            <p className="text-sm">이 날에는 일정이 없습니다</p>
                                        </div>
                                    ) : (
                                        selectedEvents.map(evt => {
                                            const badge = getStatusBadge(evt.status);
                                            return (
                                                <div key={evt.id} className="p-3 rounded-xl transition-all"
                                                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-color)' }}>
                                                    <div className="flex items-start gap-2">
                                                        <div className="w-1 h-8 rounded-full flex-shrink-0 mt-0.5" style={{ background: evt.color }} />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                                                                    style={{
                                                                        background: evt.source === 'scheduler' ? 'rgba(59,130,246,0.12)' : 'rgba(124,58,237,0.12)',
                                                                        color: evt.source === 'scheduler' ? '#3b82f6' : '#7c3aed',
                                                                    }}>
                                                                    {getSourceIcon(evt.source)} {evt.source === 'scheduler' ? '스케줄러' : '플로우'}
                                                                </span>
                                                                {badge && (
                                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                                                                        style={{ background: badge.bg, color: badge.text }}>
                                                                        {badge.label}
                                                                    </span>
                                                                )}
                                                                {evt.priority === 'urgent' && (
                                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                                                                        style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                                                                        🔥 긴급
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                                                {evt.title.replace(/^[■*\s]+/, '')}
                                                            </p>
                                                            <div className="flex items-center gap-2 mt-1 text-[11px]" style={{ color: 'var(--text-dim)' }}>
                                                                {evt.projectName && <span>{evt.projectName}</span>}
                                                                {evt.assignee && <span>· {evt.assignee}</span>}
                                                            </div>
                                                            {typeof evt.progress === 'number' && evt.progress > 0 && (
                                                                <div className="mt-2">
                                                                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                                                                        <div className="h-full rounded-full transition-all"
                                                                            style={{ width: `${evt.progress}%`, background: evt.color }} />
                                                                    </div>
                                                                    <span className="text-[10px] mt-0.5 block" style={{ color: 'var(--text-dim)' }}>
                                                                        {evt.progress}% 완료
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
